import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { editCompany } from "@/api/editCompany/editCompanyApi";
import { fetchIntegrationsStatusAction } from "@/modules/hub/features/UserAppSettings/slices/integrationsStatusSlice";
import { fetchCompanyProfileAction } from "@/modules/hub/features/UserAppSettings/slices/companyProfileSlice";

export type Integration = "servicetitan" | "quickbooks_online" | "file_import";

export type DataSourceKey = "quickbooks" | "servicetitan";

export const INTEGRATION_LABELS: Record<Integration, string> = {
  file_import: "File Import",
  servicetitan: "ServiceTitan",
  quickbooks_online: "QuickBooks Online",
};

const resolveRouteSource = (pathname: string): DataSourceKey | null => {
  if (pathname.endsWith("/servicetitan")) return "servicetitan";
  if (pathname.endsWith("/quickbooks")) return "quickbooks";
  return null;
};

export const useIntegrations = () => {
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const routeSource = resolveRouteSource(pathname);

  const { companyProfile } = useAppSelector((state) => state.companyProfile);
  const { userAppSettings } = useAppSelector((state) => state.userAppSettings);
  const { integrationsStatus } = useAppSelector(
    (state) => state.integrationsStatus,
  );

  const [expandedSources, setExpandedSources] = useState<DataSourceKey[]>(
    routeSource ? [routeSource] : [],
  );
  const [hasResolvedDefaults, setHasResolvedDefaults] = useState(!!routeSource);
  const [switchIntegrationTarget, setSwitchIntegrationTarget] =
    useState<Integration | null>(null);
  const [isSwitching, setIsSwitching] = useState(false);

  useEffect(() => {
    dispatch(fetchCompanyProfileAction());
    dispatch(fetchIntegrationsStatusAction());
  }, [dispatch]);

  const activeIntegration = integrationsStatus?.activeIntegration as
    | Integration
    | undefined;
  const isQuickBooksConnected =
    integrationsStatus?.quickBooksOnlineIntegration?.isConnected ?? false;
  const isQuickBooksConfigured =
    integrationsStatus?.quickBooksOnlineIntegration?.isConfigured ?? false;
  const isServiceTitanConfigured =
    integrationsStatus?.serviceTitanIntegration?.isConfigured ?? false;
  const isServiceTitanMappingComplete =
    integrationsStatus?.serviceTitanIntegration
      ?.isBusinessUnitMappingComplete ?? false;

  // Once status is known, open whichever sources still need attention. A route
  // that names a source wins, and a user toggle stops this from running again.
  useEffect(() => {
    if (hasResolvedDefaults || !integrationsStatus) return;

    const incomplete: DataSourceKey[] = [];
    if (!isQuickBooksConnected || !isQuickBooksConfigured) {
      incomplete.push("quickbooks");
    }
    if (!isServiceTitanConfigured) {
      incomplete.push("servicetitan");
    }

    setExpandedSources(incomplete);
    setHasResolvedDefaults(true);
  }, [
    hasResolvedDefaults,
    integrationsStatus,
    isQuickBooksConfigured,
    isQuickBooksConnected,
    isServiceTitanConfigured,
  ]);

  useEffect(() => {
    if (!routeSource) return;
    document
      .getElementById(routeSource)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [routeSource]);

  const toggleSource = (source: DataSourceKey) => {
    setHasResolvedDefaults(true);
    setExpandedSources((previous) =>
      previous.includes(source)
        ? previous.filter((item) => item !== source)
        : [...previous, source],
    );
  };

  const handleSelectIntegration = (target: Integration) => {
    if (target === activeIntegration) return;
    setSwitchIntegrationTarget(target);
  };

  const closeSwitchModal = () => {
    setSwitchIntegrationTarget(null);
  };

  const handleConfirmSwitch = async () => {
    if (!switchIntegrationTarget || !companyProfile) return;
    setIsSwitching(true);
    try {
      await editCompany(companyProfile.uuid, {
        activeIntegration: switchIntegrationTarget,
      });
      dispatch(fetchCompanyProfileAction(false));
      dispatch(fetchIntegrationsStatusAction(false));
      setSwitchIntegrationTarget(null);
    } finally {
      setIsSwitching(false);
    }
  };

  return {
    activeIntegration,
    canSwitchIntegration: integrationsStatus?.canSwitchIntegration ?? false,
    closeSwitchModal,
    companyProfile,
    expandedSources,
    handleConfirmSwitch,
    handleSelectIntegration,
    intacctId: userAppSettings?.intacctId,
    integrationsStatus,
    isQuickBooksConfigured,
    isQuickBooksConnected,
    isServiceTitanConfigured,
    isServiceTitanMappingComplete,
    isSwitching,
    switchIntegrationTarget,
    toggleSource,
  };
};
